import {
  useLayoutEffect,
  useMemo,
  useRef,
  type Dispatch,
  type SetStateAction,
} from "react";
import Box from "@mui/material/Box";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import {
  type GridColumnVisibilityModel,
  useGridApiContext,
} from "@mui/x-data-grid-premium";
import { getManageColumnsSwitchSx, useBrandThemeColors } from "../../branding";

const DEFAULT_EXCLUDED_FIELDS = new Set(["actions", "__check__"]);

export type DataGridManageColumnsPanelProps = Readonly<{
  columnVisibilityModel: GridColumnVisibilityModel;
  setColumnVisibilityModel: Dispatch<SetStateAction<GridColumnVisibilityModel>>;
  scrollTopRef: { current: number };
  lockedFields?: ReadonlySet<string>;
  getColumnLabel?: (field: string, headerName?: string | null) => string;
  title?: string;
}>;

function defaultGetColumnLabel(
  field: string,
  headerName?: string | null,
): string {
  const trimmed = headerName?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : field;
}

/** Manage Columns panel — Switch toggles, aligned with Regulations homepage table. */
export function DataGridManageColumnsPanel({
  columnVisibilityModel,
  setColumnVisibilityModel,
  scrollTopRef,
  lockedFields = new Set<string>(),
  getColumnLabel = defaultGetColumnLabel,
  title = "Manage Columns",
}: DataGridManageColumnsPanelProps) {
  const apiRef = useGridApiContext();
  const { primaryColor } = useBrandThemeColors();
  const manageColumnsSwitchSx = useMemo(
    () => getManageColumnsSwitchSx(primaryColor),
    [primaryColor],
  );

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const allColumns = apiRef.current?.getAllColumns() ?? [];
  const isManageableColumn = (field: string) =>
    !DEFAULT_EXCLUDED_FIELDS.has(field);
  const manageableColumns = allColumns.filter((c: { field: string }) =>
    isManageableColumn(c.field),
  );
  const toggleableColumns = manageableColumns.filter(
    (c: { field: string }) => !lockedFields.has(c.field),
  );
  const allToggleableVisible = toggleableColumns.every(
    (col: { field: string }) => columnVisibilityModel[col.field] !== false,
  );
  const rememberScrollTop = () => {
    scrollTopRef.current =
      scrollContainerRef.current?.scrollTop ?? scrollTopRef.current;
  };

  useLayoutEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollTopRef.current;
    }
  }, [columnVisibilityModel, scrollTopRef]);

  return (
    <Box sx={{ width: 250, bgcolor: "background.paper" }}>
      <Box sx={{ p: "12px 16px", borderBottom: "1px solid", borderColor: "divider" }}>
        <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#262626" }}>
          {title}
        </Typography>
      </Box>
      <Box
        ref={scrollContainerRef}
        onScroll={(event) => {
          scrollTopRef.current = event.currentTarget.scrollTop;
        }}
        sx={{ maxHeight: 400, overflowY: "auto", py: 1 }}
      >
        {allColumns.map((column: { field: string; headerName?: string }) => {
          if (!isManageableColumn(column.field)) return null;

          const locked = lockedFields.has(column.field);
          const isVisible =
            locked || columnVisibilityModel[column.field] !== false;
          return (
            <Box
              key={column.field}
              sx={{
                px: 2,
                py: 0.8,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Switch
                size="small"
                disabled={locked}
                checked={isVisible}
                onChange={() => {
                  rememberScrollTop();
                  setColumnVisibilityModel((prev) => ({
                    ...prev,
                    [column.field]: !isVisible,
                  }));
                }}
                sx={manageColumnsSwitchSx}
              />
              <Typography
                sx={{
                  fontSize: "14px",
                  color: locked ? "action.disabled" : "#595959",
                }}
              >
                {getColumnLabel(column.field, column.headerName)}
              </Typography>
            </Box>
          );
        })}
      </Box>
      <Box
        sx={{
          p: "12px 16px",
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Switch
          size="small"
          checked={allToggleableVisible}
          onChange={(e) => {
            rememberScrollTop();
            const checked = e.target.checked;
            const newModel: GridColumnVisibilityModel = {};
            allColumns.forEach((col: { field: string }) => {
              if (!isManageableColumn(col.field)) return;
              if (lockedFields.has(col.field)) {
                newModel[col.field] = true;
              } else {
                newModel[col.field] = checked;
              }
            });
            setColumnVisibilityModel(newModel);
          }}
          sx={manageColumnsSwitchSx}
        />
        <Typography sx={{ fontSize: "14px", color: "#262626", fontWeight: 500 }}>
          Show/Hide All
        </Typography>
      </Box>
    </Box>
  );
}
